import {useShowProjectQuery} from 'features/studio/projects/store/show-project';
import {useSaveRelatedProductsMutation} from 'features/studio/projects/store/save-related-products';
import {useCurrentSubject} from 'features/studio/projects/utils/use-current-subject';
import {Subject} from 'features/hylian-shield/types';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router';
import {parseValidationErrors} from 'services/api/utils/parse-error';
import {ValidationErrors} from 'services/api/types';
import toast from 'store/ui/actions/toast';
import {ObjectId} from 'types/object-id';
import {Option} from 'types/option';
import {CompactVariant} from 'types/compact-variant';
import makeId from 'utils/make-id';

export type RelatedProductRow = {
  id: ObjectId;
  variant?: CompactVariant;
};

type RowMap = Record<ObjectId, RelatedProductRow>;

export const useRelatedProductsForm = (): IRelatedProductsForm => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {id} = useParams();

  const {data, isLoading: isLoadingProject} = useShowProjectQuery({id});
  const project = data?.data;
  const subject = useCurrentSubject(project?.project_type);

  const [rows, setRows] = useState<RowMap>({});

  useEffect(() => {
    if (!project?.related_products) return;

    const seeded: RowMap = {};
    project.related_products.forEach(rp => {
      const rowId = makeId();
      seeded[rowId] = {id: rowId, variant: rp.variant};
    });
    setRows(seeded);
  }, [project?.related_products]);

  const [save, {isLoading: isSubmitting, error}] =
    useSaveRelatedProductsMutation();
  const serverError = parseValidationErrors(error);

  const addRow = () => {
    const row: RelatedProductRow = {id: makeId()};
    setRows(prev => ({...prev, [row.id]: row}));
  };

  const removeRow = (rowId: ObjectId) => {
    setRows(prev => {
      const next = {...prev};
      delete next[rowId];
      return next;
    });
  };

  const updateRow = (row: RelatedProductRow, variant?: CompactVariant) => {
    setRows(prev => ({...prev, [row.id]: {...prev[row.id], variant}}));
  };

  const filterOption = (option: Option<CompactVariant>) => {
    return !Object.values(rows).some(r => r.variant?.id === option.value);
  };

  const handleSubmit = async () => {
    const variants = Object.values(rows)
      .filter(r => r.variant?.id)
      .map(r => ({variant_id: Number(r.variant!.id)}));

    const result = await save({id, variants});

    if ('data' in result) {
      dispatch(toast('success', t('common.changesCommited')));
    } else {
      const {message} = parseValidationErrors(result.error);
      dispatch(toast('error', message));
    }
  };

  return {
    rows,
    subject,
    isLoadingProject,
    isSubmitting,
    validationErrors: serverError.errors,
    addRow,
    removeRow,
    updateRow,
    filterOption,
    submit: handleSubmit,
  };
};

export default interface IRelatedProductsForm {
  rows: RowMap;
  subject: Subject;
  isLoadingProject: boolean;
  isSubmitting: boolean;
  validationErrors: ValidationErrors;
  addRow: () => void;
  removeRow: (rowId: ObjectId) => void;
  updateRow: (row: RelatedProductRow, variant?: CompactVariant) => void;
  filterOption: (option: Option<CompactVariant>) => boolean;
  submit: () => Promise<void>;
}
